import dayjs from 'dayjs';
import { Message, MessageBox } from 'element-ui';

/**
 * 处理逗号分割的查询字段, 逗号、空格、换行符分隔的字符串转数组
 * 支持通过excel复制数据－粘贴到文本框，系统自动拼接逗号隔开，进行查询
 * @param {string} value 查询字段的值
 * @returns {string[]}
 */
export function searchFieldSeperated (value) {
    return (value || '')
        .replace('，', ',')
        .replace(/\s+/g, ',')
        .split(/[,，]/)
        .map(no => no.trim())
        .filter(no => no);
}

/**
 * @description 校验查询时间跨度不能超过指定日期
 * @param {Object} searchData 查询参数
 * @param {String} sDateField 查询起始时间字段名
 * @param {String} eDateField 查询结束时间字段名
 * @param {{ span: number, unit: dayjs.ManipulateType, noMessage: boolean, addOneDay: boolean }} 配置时间范围限制
 */
export const validateMaxDate = (searchData, sDateField, eDateField, options, searchType = '时间') => {
    if (searchData[sDateField] && searchData[eDateField]) {
        if (!options) {
            const sDateTimeStamp = new Date(searchData[sDateField]).getTime();
            const eDateTimeStamp = new Date(searchData[eDateField]).getTime();
            const maxDay = 1000 * 60 * 60 * 24 * 63; // 按31天来算，组件问题，得加多一天
            if (eDateTimeStamp - sDateTimeStamp > maxDay) {
                Message.warning(`按${searchType}查询或导出时，查询或导出时间跨度不可超过两个月`);
                return false;
            }
        } else {
            const { span = 2, unit = 'month', noMessage = false, addOneDay = true } = options || {};
            const sDate = dayjs(searchData[sDateField]);
            const eDate = dayjs(searchData[eDateField]);
            let addedTime = sDate.startOf('day')
                .add(span, unit);
            if (addOneDay) {
                addedTime = addedTime.add(2, 'day'); // OTP时间选择组件问题，多加一天;大小月 无法选择问题，再多加一天
            }
            const eTime = eDate.endOf('day');
            const isValid = addedTime.isAfter(eTime);
            if (!isValid && !noMessage) {
                const unitNames = {
                    day: '天',
                    month: '个月',
                    week: '周',
                    year: '年'
                };
                Message.warning(`按${searchType}查询或导出时，查询或导出时间跨度不可超过${span}${unitNames[unit]}`);
            }
            return isValid;
        }
    }
    return true;
};

/**
 * @description 校验查询时间跨度不能超过指定日期
 * @param {Object} searchData 查询参数
 * @param {String} sDateField 查询起始时间字段名
 * @param {String} eDateField 查询结束时间字段名
 * @param {String} maxSearchDay 最大时间跨度
 */
export const validateSearchTime = (searchData, sDateField, eDateField, maxSearchDay = 31, searchType = '时间') => {
    if (searchData[sDateField] && searchData[eDateField]) {
        const sDateTimeStamp = new Date(searchData[sDateField]).getTime();
        const eDateTimeStamp = new Date(searchData[eDateField]).getTime();
        const maxDay = 1000 * 60 * 60 * 24 * (maxSearchDay + 1); // OTP时间组件原因，得加多一天
        if (eDateTimeStamp - sDateTimeStamp > maxDay) {
            Message.warning(`按${searchType}查询或导出时，查询或导出时间跨度不可超过${maxSearchDay}天`);
            return false;
        }
    }
    return true;
};

/**
 * @description 按钮操作 二次确认的消息提示
 * @param {String} tipsInfo 提示信息
 * @param {String} api 调用的接口
 * @param {Array/String/Object} params 接口参数
 * @param {String} successTips 操作成功的提示
 * @param {Object} that 接收函数调用者的 this
 */
export const ReConfirmTip = (tipsInfo = '此操作将永久修改该数据, 是否继续？', api, params, successTips = '操作成功', that) => {
    MessageBox.confirm(tipsInfo, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
                instance.confirmButtonLoading = true;
                instance.confirmButtonText = '执行中...';
                api(params)
                    .then(res => {
                        if (res && +res.code === 0) {
                            Message.success(successTips);
                            done();
                        }
                    })
                    .catch((errMsg) => {
                        Message.error(errMsg);
                    })
                    .finally(() => {
                        instance.confirmButtonLoading = false;
                        done();
                        that.getList();
                    });
            } else {
                done();
            }
        }
    }).catch(() => { }); // 添加错误捕获
};

/**
 * @description 按钮操作 二次确认的消息提示
 * @param {String} tipsInfo 提示信息
 * @param {String} api 调用的接口
 * @param {Array/String/Object} params 接口参数
 * @param {String} successTips 操作成功的提示
 * @param {Object} that 接收函数调用者的 this
 * @param {Object} functionName 调用者的方法名
 */
export const confirmTip = (tipsInfo = '此操作将永久修改该数据, 是否继续？', api, params, successTips = '操作成功', that = null, functionName = null) => {
    MessageBox.confirm(tipsInfo, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        beforeClose: (action, instance, done) => {
            if (action === 'confirm') {
                instance.confirmButtonLoading = true;
                instance.confirmButtonText = '执行中...';
                api(params)
                    .then(res => {
                        if (res && +res.code === 0) {
                            Message.success(successTips);
                            done();
                            that && functionName && that[functionName]();
                        }
                    })
                    .catch((errMsg) => {
                        Message.error(errMsg);
                    })
                    .finally(() => {
                        instance.confirmButtonLoading = false;
                        done();
                    });
            } else {
                done();
            }
        }
    }).catch(() => { }); // 添加错误捕获
};
